<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

import NextButton from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Modal from '../../../../components/Modal.vue';
import CannedMarkdownEditor from './CannedMarkdownEditor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    TagMultiSelectComboBox,
    Modal,
    CannedMarkdownEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      addCanned: {
        showLoading: false,
        message: '',
      },
      selectedInboxIds: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
    }),
    inboxOptions() {
      return (this.inboxes || []).map(inbox => ({
        value: Number(inbox.id),
        label: inbox.name,
      }));
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
  },
  methods: {
    syncCanonicalContent() {
      this.content = String(this.content || '').replace(/\r\n/g, '\n');
      this.v$.content.$touch();
    },
    normalizedInboxIds() {
      return this.selectedInboxIds.map(id => Number(id)).filter(Boolean);
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.selectedInboxIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    addCannedResponse() {
      this.syncCanonicalContent();
      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          inbox_ids: this.normalizedInboxIds(),
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            Inboxes
          </label>
          <TagMultiSelectComboBox
            v-model="selectedInboxIds"
            class="mb-2"
            :options="inboxOptions"
            placeholder="Global (all inboxes)"
            search-placeholder="Buscar inbox"
            empty-state="Nenhum inbox encontrado"
          />
          <p class="text-xs text-n-slate-10 mb-2">
            Deixe vazio para Global (todos os inboxes).
          </p>
        </div>

        <div class="w-full canned-content-field">
          <div class="canned-content-header">
            <label :class="{ error: v$.content.$error }">
              {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
            </label>
          </div>
          <div class="editor-wrap">
            <CannedMarkdownEditor
              v-model="content"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @input="syncCanonicalContent"
              @blur="syncCanonicalContent"
            />
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
.canned-content-field {
  position: relative;
}

.canned-content-header {
  min-height: 2rem;
  display: flex;
  align-items: center;
  margin-bottom: 0.375rem;
}

.editor-wrap :deep(.canned-markdown-editor) {
  min-height: 12.5rem;
}
</style>
